import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import { markBreaks, phraseSegments, stripBreakMarks } from "@noiman/ui/lg";
import { HandIcon } from "../luagate-ui";
import { LgPhrase } from "../luagate-ui/LgPhrase";
import "./page-band.css";

/** パンくずの 1 段。to の無い段は字だけ */
export type PageBandCrumb = { readonly label: string; readonly to?: string };

/*
  英字 (HTTP、URL) と日本語のあいだの普通の空白で折ると、英字の語だけが行の端に残ったり
  (「よく使う HTTP / の」)、空白のあとの日本語が 1 字だけの行になったりする (「HTTP / の」の次の行が「を」だけ)。
  帯の題は狭い列で折れやすいので、この空白を折らない空白 (U+00A0) に替える
  (certification-parts.tsx の keepAsciiWithJa と同じ考え方。帯はどの頁の題でも通るので、ここに 1 つ置く)
*/
const ASCII_JA_SPACE = /([A-Za-z0-9)）])\s+(?=[぀-ヿ一-鿿])|([぀-ヿ一-鿿、。）」])\s+(?=[A-Za-z0-9])/g;
function keepAsciiWithJaSpace(text: string): string {
  return text.replace(ASCII_JA_SPACE, (_m, a: string | undefined, b: string | undefined) => `${a ?? b} `);
}

/*
  閉じの括弧と引用符 (」』）”) と「！」「？」のあとは、keep-all でも折れる所になる。すぐ後ろが仮名 (助詞) だと「“作る側の視点” / を学ぶ」と
  助詞から始まる行ができる (10/25 /lab の受講生の声の題)。そこに語をつなぐ印 (U+2060) を入れて折らない
*/
const CLOSE_BEFORE_KANA = /([」』）)”’】！？!?])(?=[ぁ-ゖ])/g;
/** 区切りの記号 (記事名の「｜」、「業務×プログラミング」の「×」) は前の語に付け、行の頭に来ないようにする。折れるのは記号のあと */
const SEPARATOR = /\s*([｜×])\s*/g;
/** 英数字をハイフンでつないだ語 (justify-content、Human-in-the-loop) は、ハイフンのあとで折らない */
const ASCII_HYPHEN = /([A-Za-z0-9])-(?=[A-Za-z0-9])/g;
/** 頭の文節がこの字数 (空白を除く) 以下なら、次の文節と折らない塊にする。「講師 / インタビュー」のような 2 字だけの行を作らない */
const SHORT_HEAD_LENGTH = 2;

/** 帯の題の文節。頭の 1〜2 字の文節は次の文節とつないで 1 つにする (つないだかどうかも返す) */
function bandSegments(title: string): { text: string; segs: string[]; boundHead: boolean } {
  const text = keepAsciiWithJaSpace(title)
    .replace(CLOSE_BEFORE_KANA, "$1\u2060")
    .replace(SEPARATOR, "\u2060$1")
    .replace(ASCII_HYPHEN, "$1-\u2060");
  const segs = phraseSegments(text);
  if (segs.length < 2 || segs[0].replace(/\s/g, "").length > SHORT_HEAD_LENGTH) return { text, segs, boundHead: false };
  // 次の文節の頭の語 (空白の手前) までをつなぐ。文節が空白をまたぐ (「つる / ぎ町立半田病院 ランサムウェア被害」) とき、空白のあとは折れるまま残す
  const [, word = "", rest = ""] = segs[1].match(/^(\S*)([\s\S]*)$/) ?? [];
  return { text, segs: [segs[0] + word, ...(rest ? [rest] : []), ...segs.slice(2)], boundHead: true };
}

/** 帯の題の字を、折れ方の決まり (字の間の印は LgPhrase が wbr と折らない塊に替え、HTML の字には残さない) (文節で折る、約物や助詞から行を始めない、1〜2 字だけの行を作らない) に合わせて LgPhrase に渡す */
function bandTitle(title: string): ReactNode {
  const { text, segs, boundHead } = bandSegments(title);
  if (!boundHead) return text;
  return (
    <>
      <span className="lg-nowrap">{markBreaks(segs[0])}</span>
      {segs.slice(1).join("")}
    </>
  );
}

/** これより長い題は長い題 (#3232 の公開の頁の決め) */
const LONG_TITLE_LENGTH = 22;
/**
 * 折れない塊 (文節、折らない空白でつないだ語、長いカタカナ語) がこの幅 (和字の数、英数字は 0.6 字) を超えたら、
 * 短い題でも長い題の形にする。スマホの 55% の列は 360px の画面で 24px の字が 7 字半しか入らず、
 * 「クロスサイトスクリプティング」「SQL インジェクション」が列からはみ出して絵に重なった (10/25 /security)
 */
const MAX_RUN_IN_COLUMN = 7;
// 折らない空白 (U+00A0) は塊の中に残るので、普通の空白と同じ 0.3 字で数える。語をつなぐ印 (U+2060) は幅が無い
const runWidth = (run: string) =>
  [...run.replace(/[ \t\n]/g, "")].reduce((n, ch) => n + (ch === "\u2060" ? 0 : ch === "\u00a0" ? 0.3 : ch <= "\u007f" ? 0.6 : 1), 0);

/**
 * 帯の題を長い題の形 (スマホは帯いっぱいに広げて 20px、広い画面は左の半分で 28px。page-band.css の .pub-band--long-title) で出すか。
 * 字数が多い題と、狭いスマホの題の列に入らない折れない塊を持つ題
 */
export function isLongBandTitle(title: string): boolean {
  if (title.length > LONG_TITLE_LENGTH) return true;
  // 折れない塊に分ける。文節の境目と普通の空白で切り、折らない空白と語をつなぐ印 (U+00A0、U+2060) をまたぐ所はつなぐ
  const runs: string[] = [];
  for (const seg of bandSegments(title).segs) {
    seg.split(/[ \t]+/).filter(Boolean).forEach((word, i) => {
      const prev = runs[runs.length - 1];
      if (i === 0 && prev !== undefined && !/\s$/.test(prev) && !/^\s/.test(seg) && (/[\u00a0\u2060]$/.test(prev) || /^[\u00a0\u2060]/.test(word))) runs[runs.length - 1] = prev + word;
      else runs.push(word);
    });
  }
  // LgPhrase は 1〜2 字の尾を前の語とつなぐ (「ランサムウェア被害」)
  if (runs.length >= 2 && runs[runs.length - 1].replace(/\s/g, "").length <= 2) runs.splice(-2, 2, runs[runs.length - 2] + runs[runs.length - 1]);
  return runs.some((run) => runWidth(run) > MAX_RUN_IN_COLUMN);
}

/**
 * 帯に重ねる題 (h1) とパンくず。
 * home は頭の段 (/luagate はダッシュボード、公開の頁はホーム)。字は出さず家の印にし、label は読み上げの名前にする (/luagate の LgStepCrumbs と同じ)。
 * null で出さない (その頁が home そのもののとき)。
 * crumbs は home と今の頁のあいだの段。途中の段が無い頁 (一覧の頁) は今の頁の題も最後の段として見せる (「家 › 教科書」、10/25 代表)。
 * 途中の段がある頁は題を帯の h1 に任せ、段には読み上げだけで残す (動画や記事の長い題がパンくずと h1 に 2 回並んだ)
 */
export type PageBandHeading = {
  readonly title: string;
  readonly crumbs: readonly PageBandCrumb[];
  readonly home: PageBandCrumb | null;
  /** パンくずの nav の読み上げの名前。渡さない呼び出し元は今までどおり日本語 */
  readonly crumbsLabel?: string;
};

/** 帯の絵の 1 枚。2880x400 前後の横長の絵を、帯の高さに合わせて切り抜く (切り方は page-band.css) */
export function PageBandImg({ src, night = false, eager = true }: { src: string; night?: boolean; eager?: boolean }) {
  return (
    <img
      className={night ? "lgf-band__img lgf-band__img--night" : "lgf-band__img"}
      src={src}
      alt=""
      width={2880}
      height={400}
      loading={eager ? undefined : "lazy"}
      decoding="async"
    />
  );
}

function PageBandCrumbs({ heading }: { heading: PageBandHeading }) {
  return (
    <nav className="lgf-band__crumbs" aria-label={heading.crumbsLabel ?? "パンくずリスト"}>
      <ol>
        {heading.home ? (
          <li>
            <Link to={heading.home.to ?? "/"} className="lgf-band__home" aria-label={heading.home.label}>
              <HandIcon name="nav-home" size={20} />
            </Link>
          </li>
        ) : null}
        {heading.crumbs.map((crumb, index) => (
          <Fragment key={`${index}-${crumb.label}`}>
            <li>
              <span className="lgf-band__sep" aria-hidden="true">
                ›
              </span>
              {crumb.to ? <Link to={crumb.to}>{markBreaks(crumb.label)}</Link> : <span>{markBreaks(crumb.label)}</span>}
            </li>
          </Fragment>
        ))}
        {heading.crumbs.length === 0 ? (
          <li>
            {heading.home ? (
              <span className="lgf-band__sep" aria-hidden="true">
                ›
              </span>
            ) : null}
            <span aria-current="page">{markBreaks(heading.title)}</span>
          </li>
        ) : (
          <li className="lg-sr-only" aria-current="page">
            {stripBreakMarks(heading.title)}
          </li>
        )}
      </ol>
    </nav>
  );
}

/*
  /luagate の帯 (PublicPageBand は自分で isLongBandTitle を見て .pub-band--long-title を付けるので、ここでは触らない) が
  長い題の形に切り替えるか。公開の頁と違い、/luagate の帯はパンくずも絵に重なる側なので、
  段が 1 つでも付いたら長い形にする。生成 (ダッシュボード、学習パスの既定など) の短い題で段も無い頁は今までの軽い形のまま
*/
function isLongLuagateBand(heading: PageBandHeading): boolean {
  return isLongBandTitle(heading.title) || heading.crumbs.length > 0;
}

/**
 * /luagate の帯で、PC の 44% の列に 32px の字では帯の高さ (168px) に収まらない題か。
 * 授業アーカイブの題は 87 本のうち 29 本が 36 字を超え、5〜6 行になって帯の下で切れていた (11/2)。page-band.css が 22px に下げる
 */
const XLONG_LUAGATE_TITLE_LENGTH = 36;
function isXlongLuagateTitle(heading: PageBandHeading): boolean {
  return [...stripBreakMarks(heading.title)].length > XLONG_LUAGATE_TITLE_LENGTH;
}

/**
 * 頁の頭の帯。横幅いっぱいの絵に、左の淡いかすみの上でパンくずと題 (h1) を重ねる。
 * /luagate (LgPageBandOf) と公開の頁 (PublicPageBand) の両方がこれを出す。形は page-band.css の 1 枚だけ (.lg の中で効く)。
 * art は PageBandImg (昼と夜を出し分けるなら呼ぶ側で包む)。heading が無ければ絵だけの帯
 */
export function PageBand({ name, art, heading }: { name: string; art: ReactNode; heading?: PageBandHeading }) {
  return (
    <div
      className="lgf-band"
      data-band={name}
      data-band-long={heading && isLongLuagateBand(heading) ? "" : undefined}
      data-band-xlong={heading && isXlongLuagateTitle(heading) ? "" : undefined}
    >
      {art}
      {heading ? (
        <div className="lgf-band__overlay">
          <PageBandCrumbs heading={heading} />
          <h1 className="lgf-band__title">
            <LgPhrase>{bandTitle(heading.title)}</LgPhrase>
          </h1>
        </div>
      ) : null}
    </div>
  );
}
